/**
 * Week-grid view for a `MenuPlan` (issue #156 sub-PR #5).
 *
 * Renders the 7 ISO weekdays of `plan.week_start` as columns,
 * with each cell showing the existing menu_item(s) for that day
 * (one per combo) plus an "Add" button to create a new
 * (date, combo) slot.
 *
 * Scope this PR:
 *   - 7-column day grid driven by `plan.week_start`
 *   - per-cell render of items for that delivery_date
 *   - "Add menu item" dialog per day (creates new (date, combo)
 *     row through `menuPlanAPI.upsertItem`)
 *   - per-item edit (re-opens dialog) + soft-delete (with confirm)
 *
 * Out of scope this PR:
 *   - drag-drop combo palette → cells (deferred to sub-PR #5b
 *     once `dnd-kit` is added to package.json with explicit
 *     bundle-size + a11y review)
 *   - in-cell dish editing (item-level snapshot is auto-generated
 *     at publish; the publish flow is the right surface for
 *     deliberate dish overrides)
 *
 * Permission gating: cell-level "Add" + per-item Edit/Delete only
 * render when `menu_plans:write` is granted. Read-only viewers
 * (and operators on a non-draft plan) see the grid but no controls.
 * The detail page's `LifecycleActionsToolbar` already exposes the
 * state-machine transitions that gate when item edits are legal —
 * the week page only checks the static permission; the backend
 * enforces `plan_not_draft` via the extras-routed 409.
 */
import { useMemo, useState } from 'react';
import {
  PageShell,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Loading,
} from '@resource-kit/ui/react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Plus, Pencil } from 'lucide-react';
import { menuPlanAPI, type MenuItem, type MenuPlanStatus } from '@/services';
import { useBetterAuth } from '@hooks/useAuth';
import { PERMISSIONS } from '@/permissions';
import { queryKeys } from '@lib/queryKeys';
import { lookupEnumLabelOrWarn } from '@/lib/enum-label';
import { menuPlanStatusOptions, menuItemStatusOptions } from '../schema';
import { UpsertMenuItemDialog } from '../components/UpsertMenuItemDialog';

/** Generate the 7 ISO delivery dates for a plan starting on `weekStart`.
 *  Mirrors `backend/src/utils/date.ts` shape: UTC midnight, +1d
 *  increments. Plan invariant INV-M2-1 guarantees `weekStart` is a
 *  Monday, so positions[0]…positions[6] are Mon…Sun.
 *
 *  Exported for unit tests — the week-arithmetic is the most
 *  regression-prone part (off-by-one + month-rollover are classic
 *  bugs here).
 *
 *  Dev-mode invariant trip wire: if INV-M2-1 ever breaks (a
 *  `week_start` that isn't a Monday somehow reaches the frontend),
 *  the weekday header labels would silently misalign with the dates
 *  — the EQD §3 "silent fallback" failure mode applied to UI. The
 *  `console.warn` fires in dev + test (vitest sets
 *  `import.meta.env.DEV = true`); prod bundles strip the check.
 *  Backend stays authoritative; this is a defensive trip wire for
 *  the frontend mental model. */
export function weekDates(weekStart: string): string[] {
  const start = new Date(`${weekStart}T00:00:00Z`);
  if (import.meta.env.DEV && start.getUTCDay() !== 1) {
    // eslint-disable-next-line no-console
    console.warn(
      `[weekDates] expected ISO Monday, got UTCDay=${start.getUTCDay()} for ${weekStart}`,
    );
  }
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start.getTime() + i * 86400000);
    return d.toISOString().slice(0, 10);
  });
}

/** Render the items array grouped by `delivery_date`. Returns a
 *  `Map<date, MenuItem[]>` so a future cell render can show
 *  multiple items per day (one per combo). */
function groupByDate(items: MenuItem[]): Map<string, MenuItem[]> {
  const out = new Map<string, MenuItem[]>();
  for (const it of items) {
    const existing = out.get(it.delivery_date);
    if (existing) {
      existing.push(it);
    } else {
      out.set(it.delivery_date, [it]);
    }
  }
  return out;
}

const WEEKDAY_LABEL_KEYS = [
  'menu_plan.weekPage.weekday.monday',
  'menu_plan.weekPage.weekday.tuesday',
  'menu_plan.weekPage.weekday.wednesday',
  'menu_plan.weekPage.weekday.thursday',
  'menu_plan.weekPage.weekday.friday',
  'menu_plan.weekPage.weekday.saturday',
  'menu_plan.weekPage.weekday.sunday',
] as const;

interface UpsertTarget {
  deliveryDate: string;
  existing?: MenuItem;
}

/** Whether to render add / edit / delete UI for a plan.
 *
 *  Two-condition predicate: (a) the user holds
 *  `menu_plans:write`, AND (b) the plan is in `draft` status. The
 *  backend `upsertItem` route rejects non-draft mutations with
 *  `plan_not_draft` 409 — surfacing the buttons on a non-draft
 *  plan would lure the operator into a guaranteed 409. EQD §3.
 *
 *  Exported for unit tests: the 4-cell truth table
 *  (hasWrite × planStatus) is small enough that a focused unit
 *  test catches regressions much more cheaply than rendering the
 *  whole page through RTL.
 *
 *  `plan === undefined` (still loading) returns `false` — the
 *  loading state already shows `<Loading />` rather than the
 *  grid, so this is defense in depth. */
export function canMutateWeekPlan(
  plan: { status: MenuPlanStatus } | undefined,
  hasWritePermission: boolean,
): boolean {
  if (!plan) return false;
  return hasWritePermission && plan.status === 'draft';
}

export function MenuPlanWeekPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { user } = useBetterAuth();
  const [upsertTarget, setUpsertTarget] = useState<UpsertTarget | null>(null);

  // Write re-gated onto `catalog:write` — `menu_plans:write` folded
  // into the unified `catalog:*` module in the backend redesign (#943).
  const hasWritePermission = user?.permissions?.includes(PERMISSIONS.CATALOG_WRITE) ?? false;

  const planQuery = useQuery({
    // Dedicated cache slot for the raw `{ plan, items }` envelope. Sharing
    // `detail(id)` with ResourceDetailPage (which caches the enveloped
    // `{ success, message, data }` form) collided within the staleTime and
    // showed "record not found" on back-navigation (#1151). `weekDetail`
    // still prefix-descends from `detail(id)`, so lifecycle mutations that
    // invalidate `detail(id)` refresh this view too.
    queryKey: queryKeys.menuPlans.weekDetail(id ?? ''),
    queryFn: () => menuPlanAPI.detail(id ?? ''),
    enabled: !!id,
  });

  // `detail` resolves the unwrapped `{ plan, items }` envelope (ADR-0006
  // §5). Per-item delete is intentionally absent: the rebuilt `/api/menu`
  // face ships no item soft-delete endpoint, so the week grid offers
  // add + edit only (re-adding a combo overwrites the slot).
  const detail = planQuery.data;
  const plan = detail?.plan;
  const items = detail?.items ?? [];
  const canMutate = canMutateWeekPlan(plan, hasWritePermission);
  const dates = useMemo(
    () => (plan?.week_start ? weekDates(plan.week_start) : []),
    [plan?.week_start],
  );
  const byDate = useMemo(() => groupByDate(items), [items]);

  if (planQuery.isLoading || !plan) {
    return <Loading />;
  }

  return (
    // #345 PR #353: the old dynamic status-key lookup would render the raw
    // i18n key literally on drift (i18next returns
    // the key when not found, no defaultValue specified). Route through
    // the SOT options exported by `../schema.tsx` — sibling of the
    // `src/features/menu-planning/schema.tsx::menuPlanListView` fix #351
    // already landed.
    <PageShell
      title={t('menu_plan.weekPage.title', { weekStart: plan.week_start })}
      subtitle={t('menu_plan.weekPage.subtitle', {
        status: lookupEnumLabelOrWarn(plan.status, menuPlanStatusOptions, 'menu plan status'),
      })}
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-7">
        {dates.map((date, i) => {
          const items = byDate.get(date) ?? [];
          return (
            <Card key={date}>
              <CardHeader>
                <CardTitle className="text-sm font-semibold">{t(WEEKDAY_LABEL_KEYS[i])}</CardTitle>
                <p className="font-mono text-xs text-muted-foreground">{date}</p>
              </CardHeader>
              <CardContent className="space-y-2">
                {items.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {t('menu_plan.weekPage.emptyDay')}
                  </p>
                ) : (
                  items.map((item) => (
                    <div key={item.id} className="rounded-md border border-border p-2 text-xs">
                      <p className="font-mono">{item.combo_id}</p>
                      <p className="text-muted-foreground">
                        {/* #345 PR #353: same raw-key-leak class as
                            the `subtitle` above + `MenuItemsCard:194`.
                            Route through the SOT options table. */}
                        {lookupEnumLabelOrWarn(
                          item.status,
                          menuItemStatusOptions,
                          'menu item status',
                        )}
                      </p>
                      {canMutate && (
                        <div className="mt-1 flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setUpsertTarget({
                                deliveryDate: date,
                                existing: item,
                              })
                            }
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))
                )}
                {canMutate && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={() => setUpsertTarget({ deliveryDate: date })}
                    data-testid={`menu-plan-week-add-${date}`}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    {t('menu_plan.weekPage.addButton')}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      {upsertTarget && (
        <UpsertMenuItemDialog
          open={!!upsertTarget}
          onClose={() => setUpsertTarget(null)}
          planId={plan.id}
          deliveryDate={upsertTarget.deliveryDate}
          existing={upsertTarget.existing}
        />
      )}
    </PageShell>
  );
}
